import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { subtaskCommentsApi } from "@/features/tasks/api/subtask_comments";
import { subKeys } from "@/features/tasks/hooks/useSubTasks";

const stcKeys = {
  list: (subtaskId: string) => ["subtask-comments", subtaskId] as const,
};

export function useSubTaskComments(taskId: string, subtaskId: string) {
  return useQuery({
    queryKey: stcKeys.list(subtaskId),
    queryFn: () => subtaskCommentsApi.list(taskId, subtaskId),
    enabled: !!subtaskId,
  });
}

export function useCreateSubTaskComment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ content, mentionIds, replyTo }: { content: string; mentionIds?: string[]; replyTo?: string | null }) =>
      subtaskCommentsApi.create(taskId, subtaskId, content, mentionIds, replyTo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: stcKeys.list(subtaskId) });
      // Refresh subtask list to update comments_count
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      // Refresh notifications (e.g. self-mention)
      qc.invalidateQueries({ queryKey: ["task-notifications"] });
    },
  });
}

export function useUpdateSubTaskComment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content, mentionIds }: { id: string; content: string; mentionIds?: string[] }) =>
      subtaskCommentsApi.update(taskId, subtaskId, id, content, mentionIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: stcKeys.list(subtaskId) });
    },
  });
}

export function useDeleteSubTaskComment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      subtaskCommentsApi.delete(taskId, subtaskId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: stcKeys.list(subtaskId) });
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
    },
  });
}
